import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import dayjs from 'dayjs';
import {
  Job,
  PaginatedJobResponse,
  CreateJobInput,
  JobStatus,
  UpdateJobInput,
  QuestionnaireInput,
  Questionnaire,
} from '../generated/gql-types';
import { jobActions } from './job.action';
import { removeOne, upsertMany, upsertOne } from '../util/zustand.util';
import { unassignWorkerFromJobEffect } from './job.effect';
import { isJobActiveToday } from '../util/job.util';
import type { AssignmentRateOverrides } from './assignmentRateOverrides.type';

interface JobState {
  jobs: Job[];
  jobPostingQuestionnaire: Questionnaire | null;
  setJobs: (jobs: Job[]) => void;
  fetchJobById: (
    jobId: string,
    options?: { suppressGlobalLoading?: boolean },
  ) => Promise<Job | null>;
  fetchAllJobsForClientByStatus: (
    userId: string,
    status: JobStatus,
  ) => Promise<PaginatedJobResponse>;
  fetchAllJobsForTenant: (
    tenantId: string,
    status?: JobStatus,
    limit?: number,
    lastEvaluatedKey?: string,
  ) => Promise<PaginatedJobResponse>;
  fetchAllJobsForTenantByDateRange: (
    tenantId: string,
    startDate: string,
    endDate: string,
    status?: JobStatus,
    limit?: number,
    lastEvaluatedKey?: string,
    options?: {
      suppressGlobalLoading?: boolean;
      includeApplicationCounts?: boolean;
    },
  ) => Promise<PaginatedJobResponse>;
  fetchAllJobsForClient: (
    clientId: string,
    limit?: number,
    lastEvaluatedKey?: string,
  ) => Promise<PaginatedJobResponse>;
  fetchAllJobsForClientByDateRange: (
    clientId: string,
    startDate: string,
    endDate: string,
    status?: JobStatus,
    limit?: number,
    lastEvaluatedKey?: string,
    options?: {
      suppressGlobalLoading?: boolean;
    },
  ) => Promise<PaginatedJobResponse>;
  fetchAllJobsByStatusForUserId: (
    status: JobStatus[],
    userId: string,
    limit?: number,
    lastEvaluatedKey?: string,
    options?: {
      suppressGlobalLoading?: boolean;
    },
  ) => Promise<PaginatedJobResponse>;
  getJobsByWorkerId: (workerId: string) => Job[];
  getJobsByClientId: (clientId: string) => Job[];
  getJobsActiveToday: () => Job[];
  isLoading: boolean;
  createJob: (
    job: CreateJobInput,
    questionnaire?: QuestionnaireInput,
  ) => Promise<Job | null | undefined>;
  updateJob: (jobId: string, job: any) => Promise<Job | null | undefined>;
  deleteJob: (jobId: string) => Promise<boolean>;
  cancelJob: (jobId: string, reason: string) => Promise<boolean>;
  retriggerJobNotifications: (jobId: string) => Promise<boolean>;
  assignWorkerToJob: (
    jobId: string,
    workerId: string,
    rateOverrides?: AssignmentRateOverrides,
  ) => Promise<void>;
  unassignWorkerFromJob: (jobId: string) => Promise<void>;
  getJobById: (jobId: string) => Job | null;
  getJobsSortedByStartDate: () => Job[];
  getJobsByStatus: (statuses: JobStatus[]) => Job[];
  fetchJobPostingQuestionnaire: (
    tenantId?: string,
  ) => Promise<Questionnaire | null>;
}

export const useJobStore = create<JobState>()(
  persist(
    (set, get) => ({
      //   state
      jobs: [],
      jobPostingQuestionnaire: null,
      isLoading: false,

      //   actions
      setJobs: (jobs: Job[]) =>
        set({
          jobs: jobs.filter((job): job is Job => job != null && job.id != null),
        }),

      //   mutations
      fetchJobById: async (
        jobId: string,
        options?: { suppressGlobalLoading?: boolean },
      ) => {
        const suppressGlobalLoading = options?.suppressGlobalLoading === true;
        try {
          if (!suppressGlobalLoading) {
            set({ isLoading: true });
          }
          const { data } = await jobActions.getJob(jobId);
          if (data?.getJob) {
            set((state) => ({
              jobs: upsertOne(state.jobs, data?.getJob),
            }));
            return data?.getJob;
          }
          return null;
        } catch (e) {
          console.error('Error fetching job:', e);
          return null;
        } finally {
          if (!suppressGlobalLoading) {
            set({ isLoading: false });
          }
        }
      },
      fetchAllJobsForClientByStatus: async (
        userId: string,
        status: JobStatus = JobStatus.Filled,
      ): Promise<PaginatedJobResponse> => {
        try {
          set({ isLoading: true });
          const response = await jobActions.getAllJobsByStatusForUserId(
            [status],
            userId,
          );
          const acceptedJobs =
            response?.jobs?.filter(
              (job): job is Job => job != null && job.id != null,
            ) || [];
          set({ isLoading: false });
          set((state) => ({ jobs: upsertMany(state.jobs, acceptedJobs) }));
          return response as PaginatedJobResponse;
        } catch (e) {
          console.error('Error fetching jobs:', e);
          return {
            jobs: [],
            hasMore: false,
            lastEvaluatedKey: null,
          };
        }
      },
      fetchAllJobsByStatusForUserId: async (
        status: JobStatus[],
        userId: string,
        limit?: number,
        lastEvaluatedKey?: string,
        options?: {
          suppressGlobalLoading?: boolean;
        },
      ): Promise<PaginatedJobResponse> => {
        const suppressGlobalLoading = options?.suppressGlobalLoading === true;
        try {
          if (!suppressGlobalLoading) {
            set({ isLoading: true });
          }
          const response = await jobActions.getAllJobsByStatusForUserId(
            status,
            userId,
            limit,
            lastEvaluatedKey,
          );
          const jobs =
            response?.jobs?.filter(
              (job): job is Job => job != null && job.id != null,
            ) || [];
          set((state) => ({ jobs: upsertMany(state.jobs, jobs) }));
          return {
            jobs,
            hasMore: response?.hasMore ?? false,
            lastEvaluatedKey: response?.lastEvaluatedKey ?? null,
          };
        } catch (e) {
          console.error('Error fetching jobs:', e);
          return {
            jobs: [],
            hasMore: false,
            lastEvaluatedKey: null,
          };
        } finally {
          if (!suppressGlobalLoading) {
            set({ isLoading: false });
          }
        }
      },
      fetchAllJobsForTenant: async (
        tenantId: string,
        status?: JobStatus,
        limit: number = 10,
        lastEvaluatedKey?: string,
      ): Promise<PaginatedJobResponse> => {
        try {
          set({ isLoading: true });
          const { data } = await jobActions.getAllJobsForTenant(
            tenantId,
            status,
            limit,
            lastEvaluatedKey,
          );
          const response = data?.getAllJobsForTenant;
          const newJobs =
            response?.jobs?.filter(
              (job: any): job is Job => job != null && job?.id != null,
            ) || [];
          const isInitialFetch = lastEvaluatedKey == null;
          const mergedJobs = isInitialFetch
            ? newJobs
            : upsertMany(get().jobs, newJobs);
          set({ jobs: mergedJobs });
          return {
            jobs: mergedJobs,
            hasMore: response?.hasMore ?? false,
            lastEvaluatedKey: response?.lastEvaluatedKey ?? null,
          };
        } catch (e) {
          console.error('Error fetching jobs:', e);
          return {
            jobs: [],
            hasMore: false,
            lastEvaluatedKey: null,
          };
        } finally {
          set({ isLoading: false });
        }
      },
      fetchAllJobsForTenantByDateRange: async (
        tenantId: string,
        startDate: string,
        endDate: string,
        status?: JobStatus,
        limit?: number,
        lastEvaluatedKey?: string,
        options?: {
          suppressGlobalLoading?: boolean;
          includeApplicationCounts?: boolean;
        },
      ): Promise<PaginatedJobResponse> => {
        const suppressGlobalLoading = options?.suppressGlobalLoading === true;
        const includeApplicationCounts =
          options?.includeApplicationCounts === true;
        try {
          if (!suppressGlobalLoading) {
            set({ isLoading: true });
          }
          const { data } = await jobActions.getAllJobsForTenantByDateRange(
            tenantId,
            startDate,
            endDate,
            status,
            limit,
            lastEvaluatedKey,
            includeApplicationCounts,
          );
          const jobs =
            data?.getAllJobsForTenantBetweenDates?.jobs?.filter(
              (job: any): job is Job => job != null && job?.id != null,
            ) || [];
          set((state) => ({ jobs: upsertMany(state.jobs, jobs) }));
          return {
            jobs: jobs,
            hasMore: data?.getAllJobsForTenantBetweenDates?.hasMore ?? false,
            lastEvaluatedKey:
              data?.getAllJobsForTenantBetweenDates?.lastEvaluatedKey ?? null,
          };
        } catch (e) {
          console.error('Error fetching jobs:', e);
          return {
            jobs: [],
            hasMore: false,
            lastEvaluatedKey: null,
          };
        } finally {
          if (!suppressGlobalLoading) {
            set({ isLoading: false });
          }
        }
      },
      fetchAllJobsForClient: async (
        clientId: string,
        limit?: number,
        lastEvaluatedKey?: string,
      ): Promise<PaginatedJobResponse> => {
        try {
          set({ isLoading: true });
          const { data } = await jobActions.getAllJobsForClient(
            clientId,
            limit,
            lastEvaluatedKey,
          );
          const jobs =
            data?.getAllJobsForClient?.jobs?.filter(
              (job: any): job is Job => job != null && job?.id != null,
            ) || [];
          set((state) => ({ jobs: upsertMany(state.jobs, jobs) }));
          return {
            jobs,
            hasMore: data?.getAllJobsForClient?.hasMore ?? false,
            lastEvaluatedKey:
              data?.getAllJobsForClient?.lastEvaluatedKey ?? null,
          };
        } catch (e) {
          console.error('Error fetching jobs:', e);
          set({ jobs: [] });
          return {
            jobs: [],
            hasMore: false,
            lastEvaluatedKey: null,
          };
        } finally {
          set({ isLoading: false });
        }
      },
      fetchAllJobsForClientByDateRange: async (
        clientId: string,
        startDate: string,
        endDate: string,
        status?: JobStatus,
        limit?: number,
        lastEvaluatedKey?: string,
        options?: {
          suppressGlobalLoading?: boolean;
        },
      ): Promise<PaginatedJobResponse> => {
        const suppressGlobalLoading = options?.suppressGlobalLoading === true;
        try {
          if (!suppressGlobalLoading) {
            set({ isLoading: true });
          }
          const { data } = await jobActions.getAllJobsForClientByDateRange(
            clientId,
            startDate,
            endDate,
            status,
            limit,
            lastEvaluatedKey,
          );
          const jobs =
            data?.getAllJobsForClientBetweenDates?.jobs?.filter(
              (job: any): job is Job => job != null && job?.id != null,
            ) || [];
          set((state) => ({ jobs: upsertMany(state.jobs, jobs) }));
          return {
            jobs,
            hasMore: data?.getAllJobsForClientBetweenDates?.hasMore ?? false,
            lastEvaluatedKey:
              data?.getAllJobsForClientBetweenDates?.lastEvaluatedKey ?? null,
          };
        } catch (e) {
          console.error('Error fetching jobs for client by date range:', e);
          return {
            jobs: [],
            hasMore: false,
            lastEvaluatedKey: null,
          };
        } finally {
          if (!suppressGlobalLoading) {
            set({ isLoading: false });
          }
        }
      },
      createJob: async (
        job: CreateJobInput,
        questionnaire?: QuestionnaireInput,
      ) => {
        try {
          set({ isLoading: true });
          const { data } = await jobActions.createJob(job, questionnaire);
          set({ isLoading: false });
          if (data?.createJob) {
            set((state) => ({ jobs: upsertOne(state.jobs, data?.createJob) }));
          }
          return data?.createJob;
        } catch (e) {
          console.error('Error creating job:', e);
          set({ isLoading: false });
          throw e;
        }
      },
      updateJob: async (jobId: string, input: UpdateJobInput) => {
        try {
          set({ isLoading: true });
          const { data } = await jobActions.updateJob(jobId, input);
          if (data?.updateJob) {
            set((state) => ({
              jobs: upsertOne(state.jobs, data.updateJob),
              isLoading: false,
            }));
          } else {
            set({ isLoading: false });
          }
          return data?.updateJob;
        } catch (e) {
          console.error('Error updating job:', e);
          set({ isLoading: false });
          throw e;
        }
      },
      deleteJob: async (jobId: string) => {
        try {
          const { data } = await jobActions.deleteJob(jobId);
          set((state) => ({
            jobs: removeOne(state.jobs, jobId),
          }));
          return data?.deleteJob;
        } catch (e) {
          console.error('Error deleting job:', e);
          throw e;
        }
      },
      cancelJob: async (jobId: string, reason: string) => {
        try {
          const success = await jobActions.cancelJob(jobId, reason);
          if (success) {
            const { data: jobData } = await jobActions.getJob(jobId);
            if (jobData?.getJob) {
              set((state) => ({
                jobs: upsertOne(state.jobs, jobData.getJob),
              }));
            }
          }
          return success;
        } catch (e) {
          throw e;
        }
      },
      retriggerJobNotifications: async (jobId: string) => {
        return jobActions.retriggerNotificationsForAction(jobId);
      },
      assignWorkerToJob: async (
        jobId: string,
        workerId: string,
        rateOverrides?: AssignmentRateOverrides,
      ) => {
        try {
          const { data } = await jobActions.assignUser(
            jobId,
            workerId,
            rateOverrides,
          );
          if (data?.assignUser) {
            // Update the job in local state with the new userId
            set((state) => ({
              jobs: state.jobs.map((job) =>
                job?.id === jobId
                  ? { ...job, userId: workerId, itemStatus: JobStatus.Filled }
                  : job,
              ),
            }));
          }
          return data?.assignUser;
        } catch (e) {
          console.error('Error assigning worker to job:', e);
          throw e;
        }
      },
      unassignWorkerFromJob: async (jobId: string) => {
        try {
          const job = get().getJobById(jobId);
          const unassignedUserId = job?.userId ?? undefined;

          const { data } = await jobActions.unassignUser(jobId);

          if (data?.unassignWorkerFromJob) {
            const unassignedJob = data?.unassignWorkerFromJob;

            // Update the job in local state with the unassigned job.
            set((state) => ({ jobs: upsertOne(state.jobs, unassignedJob) }));

            await unassignWorkerFromJobEffect(jobId, unassignedUserId);
          }

          return data?.unassignWorkerFromJob;
        } catch (e) {
          console.error('Error unassigning worker to job:', e);
          throw e;
        }
      },

      //   getters
      getJobsByWorkerId: (workerId: string): Job[] => {
        return get().jobs.filter((job) => job?.userId === workerId);
      },
      getJobsByClientId: (clientId: string): Job[] => {
        return get().jobs.filter((job) => job?.clientId === clientId);
      },
      getJobById: (jobId: string): Job | null => {
        return get().jobs.find((job) => job?.id === jobId) || null;
      },
      getJobsSortedByStartDate: (): Job[] => {
        return get()
          .jobs.filter((job) => job?.startDate)
          .sort((a, b) => {
            return (
              dayjs(parseInt(a.startDate || '0')).valueOf() -
              dayjs(parseInt(b.startDate || '0')).valueOf()
            );
          });
      },
      getJobsActiveToday: (): Job[] => {
        return get().jobs.filter(
          (job) =>
            job != null &&
            job.id != null &&
            job.userId != null &&
            job.itemStatus === JobStatus.Filled &&
            isJobActiveToday(job),
        );
      },
      getJobsByStatus: (statuses: JobStatus[]): Job[] => {
        return get().jobs.filter((job) =>
          statuses.includes(job?.itemStatus as JobStatus),
        );
      },
      fetchJobPostingQuestionnaire: async (tenantId?: string) => {
        try {
          const questionnaire =
            await jobActions.getJobPostingQuestionnaire(tenantId);
          set({ jobPostingQuestionnaire: questionnaire });
          return questionnaire;
        } catch (e) {
          console.error('Error fetching job posting questionnaire:', e);
          return null;
        }
      },
    }),
    {
      name: 'job-storage',
      storage: createJSONStorage(() => AsyncStorage),
      // Discards the jobs a previous build persisted here, which filled the
      // browser storage quota on large tenants.
      version: 1,
      // Jobs are not persisted. Even a single day's jobs carry long
      // descriptions and nested site/classification data, which pushes a
      // large tenant past the browser storage quota.
      partialize: (state) => ({
        jobPostingQuestionnaire: state.jobPostingQuestionnaire,
      }),
    },
  ),
);
